import { ArrowLeft, ArrowRightCircle, Banknote, CheckCircle, Copy, CreditCard, Landmark, Pencil, Printer, Receipt, Settings, SlidersHorizontal, Star, Trash2, User, X } from 'lucide-react';
import InvoicePreview from '../../../components/admin/InvoicePreview';
import PackageCardReceipt from '../../../components/admin/PackageCardReceipt';
import { getCustomerPackageById } from '../../../api/package.api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import logo from '../../../assets/img/logo.png';
import styles from '../../../assets/scss/pages/BillDetail.module.scss';
import api from '../../../config/axios-conf';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuthStore } from '../../../stores/authStore';
import { formatCurrency, formatPhoneNumber } from '../../../utils/formatters';

const STATUS_MAP = {
    paid: { label: 'Thanh toán toàn bộ', class: styles.statusPaid },
    pending: { label: 'Chưa thanh toán', class: styles.statusPending },
    partial: { label: 'Thanh toán một phần', class: styles.statusPartial },
    cancelled: { label: 'Huỷ đơn', class: styles.statusCancelled },
};

const PAYMENT_ICONS = {
    'Tiền mặt': <Banknote size={16} />,
    'Chuyển khoản': <Landmark size={16} />,
};

function formatDateTime(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

// Tính pointCash giống app
const SNAPSHOT_DATE = new Date('2026-03-21T01:27:00+07:00').getTime();

function calcPointCash(bill) {
    const checkoutTs = new Date(bill.checkoutTime).getTime();
    if (checkoutTs >= SNAPSHOT_DATE && bill.pointCash != null) {
        return parseFloat(bill.pointCash) || 0;
    }
    return (bill.pointUsed || 0) * 200;
}

export default function BillDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const fromEdit    = !!location.state?.fromEdit;
    const fromWaiting = !!location.state?.fromWaiting;
    const { hasPermission } = usePermissions();

    const user = useAuthStore((state) => state.user);

    const [bill, setBill] = useState(null);
    const [bankInfo, setBankInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [qrLoaded, setQrLoaded] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [toast, setToast] = useState(null);

    const handleCopy = async (text, successMsg, errorMsg) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setToast({ type: 'success', text: successMsg });
        } catch {
            setToast({ type: 'error', text: errorMsg });
        }
        setTimeout(() => setToast(null), 2500);
    };

    const handleCopyCode = () =>
        handleCopy(
            bill?.code || (bill?.id ? `#${bill.id}` : ''),
            'Đã copy mã hoá đơn',
            'Copy mã hoá đơn thất bại',
        );

    const invoiceRef = useRef(null);
    const handlePrint = useReactToPrint({
        contentRef: invoiceRef,
        documentTitle: bill ? `Hóa đơn ${bill.code || bill.id}` : 'Hóa đơn',
        pageStyle: `
            @page { size: auto; margin: 4mm 2mm; }
            body { margin: 0; background: white; }
        `,
    });

    // Phiếu thẻ dịch vụ (port app PackageCardReceipt)
    const [shopInfo, setShopInfo] = useState(null);
    const [showCardReceipt, setShowCardReceipt] = useState(false);
    const [cardReceiptPackages, setCardReceiptPackages] = useState([]);
    const [loadingCard, setLoadingCard] = useState(false);
    const cardReceiptRef = useRef(null);
    const handlePrintCard = useReactToPrint({
        contentRef: cardReceiptRef,
        documentTitle: bill ? `Phiếu thẻ ${bill.code || bill.id}` : 'Phiếu thẻ',
        pageStyle: `
            @page { size: auto; margin: 4mm 2mm; }
            body { margin: 0; background: white; }
        `,
    });

    const fetchBill = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/bills/${id}`);
            setBill(data.bill || data);
        } catch (err) {
            console.error('Error fetching bill:', err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchBill();
    }, [fetchBill]);

    // Fetch banking info cho QR
    useEffect(() => {
        if (!user?.shopId) return;
        api.get(`/banking-info/shop/${user.shopId}/active`)
            .then(({ data }) => {
                const active = data.data?.[0] || data[0];
                if (active) setBankInfo(active);
            })
            .catch(() => {});
        // Thông tin shop cho phiếu thẻ
        api.get(`/shops/${user.shopId}`)
            .then(({ data }) => setShopInfo(data?.shop || data))
            .catch(() => {});
    }, [user?.shopId]);

    const handleDelete = async () => {
        // Cảnh báo khi hoá đơn có thẻ dịch vụ (port app: xoá sẽ mất thẻ + lịch hẹn)
        const packages = bill?.customerPackages || [];
        let extraWarning = '';
        if (packages.length > 0) {
            extraWarning =
                '\n\nLưu ý: Hoá đơn này có thẻ dịch vụ. Xoá sẽ xoá luôn thẻ và lịch hẹn của khách.';
            if (packages.some((p) => Number(p.usedSessions || 0) > 0)) {
                extraWarning += '\n\nChú ý: Khách đã sử dụng gói dịch vụ — xoá sẽ mất lịch sử sử dụng.';
            }
        }
        if (!window.confirm(`Bạn có chắc muốn xoá hoá đơn này?${extraWarning}`)) return;
        try {
            await api.delete(`/bills/${id}`);
            navigate('/admin/bills', { replace: true });
        } catch (err) {
            alert('Xoá hoá đơn thất bại');
        }
    };

    // Mở/đóng phiếu thẻ dịch vụ (port app handleShowCardReceipt)
    const handleShowCardReceipt = async () => {
        if (showCardReceipt) {
            setShowCardReceipt(false);
            return;
        }
        setShowPreview(false);
        // Hoá đơn mua gói → dùng customerPackages trực tiếp
        if (bill?.customerPackages?.length > 0) {
            setCardReceiptPackages(bill.customerPackages);
            setShowCardReceipt(true);
            return;
        }
        // Hoá đơn dùng buổi → fetch CustomerPackage đầy đủ theo ID
        const pkgIds = [
            ...new Set((bill?.packageUsages || []).map((pu) => pu.customerPackageId).filter(Boolean)),
        ];
        if (pkgIds.length === 0) {
            setCardReceiptPackages([]);
            setShowCardReceipt(true);
            return;
        }
        setLoadingCard(true);
        try {
            const results = await Promise.all(
                pkgIds.map((pid) =>
                    getCustomerPackageById(pid)
                        .then((r) => r?.data?.data)
                        .catch(() => null),
                ),
            );
            setCardReceiptPackages(results.filter(Boolean));
        } catch {
            setCardReceiptPackages([]);
        } finally {
            setLoadingCard(false);
            setShowCardReceipt(true);
        }
    };

    if (loading) {
        return (
            <div className={styles.billDetail}>
                {/* Skeleton Header */}
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <div className={styles.skeleton} style={{ width: 38, height: 38, borderRadius: 10 }} />
                        <div className={styles.skeleton} style={{ width: 240, height: 28, borderRadius: 8 }} />
                    </div>
                    <div className={styles.headerActions}>
                        <div className={styles.skeleton} style={{ width: 80, height: 40, borderRadius: 10 }} />
                        <div className={styles.skeleton} style={{ width: 100, height: 40, borderRadius: 10 }} />
                        <div className={styles.skeleton} style={{ width: 80, height: 40, borderRadius: 10 }} />
                    </div>
                </div>

                {/* Skeleton Meta */}
                <div className={styles.meta}>
                    <div className={styles.skeleton} style={{ width: 160, height: 36, borderRadius: 10 }} />
                    <div className={styles.skeleton} style={{ width: 140, height: 20, borderRadius: 6 }} />
                    <div className={styles.skeleton} style={{ width: 120, height: 20, borderRadius: 6 }} />
                </div>

                {/* Skeleton Layout */}
                <div className={styles.layout}>
                    <div className={styles.layoutLeft}>
                        {/* Skeleton Table Card */}
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <div className={styles.skeleton} style={{ width: 160, height: 20, borderRadius: 6 }} />
                            </div>
                            <div style={{ padding: 24 }}>
                                {[1, 2, 3].map((i) => (
                                    <div
                                        key={i}
                                        style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}
                                    >
                                        <div
                                            className={styles.skeleton}
                                            style={{ width: 48, height: 48, borderRadius: 10 }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <div
                                                className={styles.skeleton}
                                                style={{ width: '60%', height: 18, borderRadius: 6, marginBottom: 8 }}
                                            />
                                            <div
                                                className={styles.skeleton}
                                                style={{ width: '40%', height: 14, borderRadius: 6 }}
                                            />
                                        </div>
                                        <div
                                            className={styles.skeleton}
                                            style={{ width: 80, height: 18, borderRadius: 6 }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={styles.layoutRight}>
                        {/* Skeleton Customer Card */}
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <div className={styles.skeleton} style={{ width: 180, height: 20, borderRadius: 6 }} />
                            </div>
                            <div style={{ padding: 24 }}>
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} style={{ marginBottom: 18 }}>
                                        <div
                                            className={styles.skeleton}
                                            style={{ width: '50%', height: 14, borderRadius: 6, marginBottom: 6 }}
                                        />
                                        <div
                                            className={styles.skeleton}
                                            style={{ width: '80%', height: 18, borderRadius: 6 }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!bill) {
        return <div className={styles.loading}>Không tìm thấy hoá đơn</div>;
    }

    const customer = bill.customer || {};
    const employee = bill.employee || {};
    const details = bill.details || [];
    // Gói đã bán + buổi đã dùng (hoá đơn checkout) — port hiển thị từ app ListBillScreen
    const soldPackages = bill.customerPackages || [];
    const packageUsages = bill.packageUsages || [];
    const usageGroups = Object.values(
        packageUsages.reduce((acc, pu) => {
            const key = `${pu.customerPackageId}__${pu.employeeId ?? 0}`;
            if (!acc[key]) {
                acc[key] = {
                    cp: pu.customerPackage,
                    employee: pu.employee,
                    count: 0,
                    recognizedRevenue: Number(pu.recognizedRevenue || 0),
                };
            }
            acc[key].count += 1;
            return acc;
        }, {}),
    );
    const statusInfo = STATUS_MAP[bill.status] || STATUS_MAP.paid;
    const paymentIcon = PAYMENT_ICONS[bill.paymentMethod] || <Banknote size={16} />;

    const originalTotal = parseFloat(bill.originalTotal) || 0;
    const discount = parseFloat(bill.discount) || 0;
    const pointUsed = bill.pointUsed || 0;
    const pointReceived = bill.pointReceived || 0;
    const pointCash = calcPointCash(bill);
    const pureDiscount = Math.max(0, discount - pointCash);
    const total = parseFloat(bill.total) || 0;
    const owe = parseFloat(bill.owe) || 0;
    const isKhachLe = customer.name === 'Khách lẻ';

    const canUpdate = hasPermission(PERMISSIONS.BILL.UPDATE);
    const canDelete = hasPermission(PERMISSIONS.BILL.DELETE);
    const canViewPhone = hasPermission(PERMISSIONS.CUSTOMER.VIEW_PHONE);
    const canViewPackages = hasPermission(PERMISSIONS.SERVICE.VIEW_LIST);
    // Click dòng gói/buổi → sang thẻ gói của khách (port app, chỉ khách thật + có quyền)
    const canNavigatePackages = !isKhachLe && !!customer.id && canViewPackages;
    const goToPackages = () =>
        canNavigatePackages && navigate(`/admin/customers/${customer.id}`, { state: { tab: 'packages' } });

    return (
        <div className={styles.billDetail}>
            {/* ===== HEADER ===== */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => fromEdit ? navigate('/admin/dashboard') : fromWaiting ? navigate('/admin/bills/waiting') : navigate(-1)}>
                        <ArrowLeft />
                    </button>
                    <h1 className={styles.headerTitle}>
                        Hoá đơn{' '}
                        <span
                            className={`${styles.headerCode} ${styles.copyable}`}
                            onClick={handleCopyCode}
                            title="Bấm để copy mã hoá đơn"
                        >
                            {bill.code || `#${bill.id}`}
                            <Copy size={14} className={styles.copyIcon} />
                        </span>
                    </h1>
                </div>
                <div className={styles.headerActions}>
                    <button className={styles.actionBtn} onClick={showCardReceipt ? handlePrintCard : handlePrint}>
                        <Printer /> In
                    </button>
                    <button
                        className={`${styles.actionBtn}${showPreview ? ` ${styles.actionBtnActive}` : ''}`}
                        title="Xem trước hoá đơn"
                        onClick={() => {
                            setShowCardReceipt(false);
                            setShowPreview((v) => !v);
                        }}
                    >
                        <Receipt /> Xem trước
                    </button>
                    {(soldPackages.length > 0 || packageUsages.length > 0) && (
                        <button
                            className={`${styles.actionBtn}${showCardReceipt ? ` ${styles.actionBtnActive}` : ''}`}
                            title="Phiếu thẻ dịch vụ"
                            onClick={handleShowCardReceipt}
                        >
                            <CreditCard /> Phiếu thẻ
                        </button>
                    )}
                    <button
                        className={styles.actionBtn}
                        title="Cài đặt hoá đơn"
                        onClick={() => navigate('/admin/settings/invoice')}
                    >
                        <Settings />
                    </button>
                    <button
                        className={styles.actionBtn}
                        title="Cài đặt máy in"
                        onClick={() => navigate('/admin/settings/printer')}
                    >
                        <SlidersHorizontal />
                    </button>
                    {canUpdate && (
                        <button
                            className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                            onClick={() => navigate(`/admin/bills/create?edit=${bill.id}`)}
                        >
                            <Pencil /> Sửa
                        </button>
                    )}
                    {canDelete && (
                        <button className={`${styles.actionBtn} ${styles.actionBtnDanger}`} onClick={handleDelete}>
                            <Trash2 /> Xoá
                        </button>
                    )}
                </div>
            </div>

            {/* ===== STATUS + META ===== */}
            <div className={styles.meta}>
                <span className={`${styles.status} ${statusInfo.class}`}>{statusInfo.label}</span>
                <span className={styles.metaDot} />
                <span className={styles.metaDate}>{formatDateTime(bill.checkoutTime)}</span>
                <span className={styles.metaDot} />
                <span className={styles.metaPayment}>
                    {paymentIcon}
                    {bill.paymentMethod || 'Tiền mặt'}
                </span>
            </div>

            {/* ===== 2 COLUMN LAYOUT ===== */}
            <div className={styles.layout}>
                {/* ===== CỘT TRÁI: Chi tiết DV / Preview ===== */}
                <div className={styles.layoutLeft}>
                    {/* InvoicePreview luôn mount để ref sẵn sàng in bất kỳ lúc nào */}
                    <div style={showPreview ? undefined : { display: 'none' }}>
                        <InvoicePreview ref={invoiceRef} bill={bill} bankInfo={bankInfo} />
                    </div>
                    {/* Phiếu thẻ dịch vụ — mount khi mở để in được */}
                    {showCardReceipt && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <span className={styles.cardTitle}>Phiếu thẻ dịch vụ</span>
                                <button className={styles.printCardBtn} onClick={handlePrintCard}>
                                    <Printer size={16} />
                                    <span>In phiếu thẻ</span>
                                </button>
                            </div>
                            <div className={styles.cardBody}>
                                {loadingCard ? (
                                    <div style={{ textAlign: 'center', padding: 24, color: '#6B7280' }}>
                                        Đang tải phiếu thẻ...
                                    </div>
                                ) : cardReceiptPackages.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: 24, color: '#6B7280' }}>
                                        Không có thẻ dịch vụ để hiển thị
                                    </div>
                                ) : (
                                    <PackageCardReceipt
                                        ref={cardReceiptRef}
                                        packages={cardReceiptPackages}
                                        bill={bill}
                                        shopInfo={shopInfo}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    {!showPreview && !showCardReceipt && (<>
                    {/* Chi tiết dịch vụ */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <span className={styles.cardTitle}>
                                Chi tiết ({details.length + soldPackages.length + usageGroups.length})
                            </span>
                        </div>
                        <table className={styles.detailsTable}>
                            <thead>
                                <tr>
                                    <th>Sản phẩm / Dịch vụ</th>
                                    <th style={{ textAlign: 'center' }}>SL</th>
                                    <th style={{ textAlign: 'right' }}>Đơn giá</th>
                                    <th style={{ textAlign: 'right' }}>Thành tiền</th>
                                </tr>
                            </thead>
                            <tbody>
                                {details.map((item, idx) => {
                                    const qty = parseFloat(item.quantity) || 0;
                                    const price = parseFloat(item.price) || 0;
                                    const lineTotal = qty * price;
                                    const variantText = item.variant?.attributeValues
                                        ?.map((av) => av.value)
                                        .join(' - ');
                                    // Giá gốc niêm yết (gạch ngang khi bán giảm) — port app
                                    const originalUnit = Number(item.variant?.retailPrice ?? item.service?.price ?? 0);
                                    const priceReduced = originalUnit > 0 && price < originalUnit;

                                    return (
                                        <tr key={item.id || idx}>
                                            <td>
                                                <div className={styles.detailService}>
                                                    {item.service?.urlImg && (
                                                        <img
                                                            src={item.service.urlImg}
                                                            alt={item.service.name}
                                                            className={styles.detailServiceImg}
                                                        />
                                                    )}
                                                    <div className={styles.detailServiceInfo}>
                                                        <span className={styles.detailServiceName}>
                                                            {idx + 1}. {item.service?.name || 'Dịch vụ'}
                                                        </span>
                                                        {variantText && (
                                                            <span className={styles.detailVariant}>{variantText}</span>
                                                        )}
                                                        {item.employee?.name && (
                                                            <span className={styles.detailEmployee}>
                                                                <User /> NV: {item.employee.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className={styles.detailQty}>
                                                {Number.isInteger(qty) ? qty : qty.toFixed(1)}
                                            </td>
                                            <td className={styles.detailPrice}>
                                                {priceReduced && (
                                                    <span
                                                        style={{
                                                            textDecoration: 'line-through',
                                                            color: '#94A3B8',
                                                            fontSize: 12,
                                                            marginRight: 6,
                                                        }}
                                                    >
                                                        {formatCurrency(originalUnit)}
                                                    </span>
                                                )}
                                                {formatCurrency(price)}
                                            </td>
                                            <td className={styles.detailTotal}>
                                                {priceReduced && (
                                                    <span
                                                        style={{
                                                            textDecoration: 'line-through',
                                                            color: '#94A3B8',
                                                            fontSize: 12,
                                                            marginRight: 6,
                                                        }}
                                                    >
                                                        {formatCurrency(originalUnit * qty)}
                                                    </span>
                                                )}
                                                {formatCurrency(lineTotal)}
                                            </td>
                                        </tr>
                                    );
                                })}

                                {/* ===== Gói đã bán ===== */}
                                {soldPackages.map((pkg, i) => {
                                    const pkgName = pkg.templateSnapshot?.name || `Gói dịch vụ ${pkg.code}`;
                                    const sellerName = pkg.sellerEmployee?.name || '';
                                    const sessionsLabel = pkg.totalSessions ? `${pkg.totalSessions} buổi` : '';
                                    const subLine = [
                                        pkg.code ? `Mã thẻ: ${pkg.code}` : '',
                                        sessionsLabel,
                                        sellerName ? `NV bán: ${sellerName}` : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' · ');
                                    const snapPrice = pkg.templateSnapshot?.price ?? pkg.package?.price;
                                    const priceOverridden =
                                        pkg.isOverridden &&
                                        snapPrice != null &&
                                        Number(pkg.totalContractValue) !== Number(snapPrice);
                                    const contractValue = Number(pkg.totalContractValue) || 0;
                                    return (
                                        <tr
                                            key={`pkg-${pkg.id}`}
                                            className={canNavigatePackages ? styles.detailRowClickable : undefined}
                                            onClick={goToPackages}
                                        >
                                            <td>
                                                <div className={styles.detailService}>
                                                    {pkg.templateSnapshot?.urlImg && (
                                                        <img
                                                            src={pkg.templateSnapshot.urlImg}
                                                            alt={pkgName}
                                                            className={styles.detailServiceImg}
                                                        />
                                                    )}
                                                    <div className={styles.detailServiceInfo}>
                                                        <span className={styles.detailServiceName}>
                                                            {details.length + i + 1}. {pkgName}
                                                        </span>
                                                        {subLine && (
                                                            <span className={styles.detailVariant}>{subLine}</span>
                                                        )}
                                                    </div>
                                                    {canNavigatePackages && (
                                                        <ArrowRightCircle
                                                            size={20}
                                                            className={styles.detailNavArrow}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                            <td className={styles.detailQty}>1</td>
                                            <td className={styles.detailPrice}>
                                                {priceOverridden && (
                                                    <span
                                                        style={{
                                                            textDecoration: 'line-through',
                                                            color: '#94A3B8',
                                                            fontSize: 12,
                                                            marginRight: 6,
                                                        }}
                                                    >
                                                        {formatCurrency(snapPrice)}
                                                    </span>
                                                )}
                                                {formatCurrency(contractValue)}
                                            </td>
                                            <td className={styles.detailTotal}>{formatCurrency(contractValue)}</td>
                                        </tr>
                                    );
                                })}

                                {/* ===== Buổi đã dùng (dùng gói) ===== */}
                                {usageGroups.map((g, i) => {
                                    const cp = g.cp;
                                    const pkgName = cp?.templateSnapshot?.name || 'Gói dịch vụ';
                                    const code = cp?.code || '';
                                    const perfName = g.employee?.name || '';
                                    const amtPerSess =
                                        g.recognizedRevenue || Number(cp?.amountPerSession || 0);
                                    const subLine = [
                                        code ? `Mã thẻ: ${code} · Dùng buổi` : 'Dùng buổi',
                                        perfName ? `NV thực hiện: ${perfName}` : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' · ');
                                    return (
                                        <tr
                                            key={`sess-${i}`}
                                            className={canNavigatePackages ? styles.detailRowClickable : undefined}
                                            onClick={goToPackages}
                                        >
                                            <td>
                                                <div className={styles.detailService}>
                                                    <div className={styles.detailServiceInfo}>
                                                        <span className={styles.detailServiceName}>
                                                            {details.length + soldPackages.length + i + 1}. {pkgName}
                                                        </span>
                                                        {subLine && (
                                                            <span className={styles.detailVariant}>{subLine}</span>
                                                        )}
                                                    </div>
                                                    {canNavigatePackages && (
                                                        <ArrowRightCircle
                                                            size={20}
                                                            className={styles.detailNavArrow}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                            <td className={styles.detailQty}>{g.count}</td>
                                            <td className={styles.detailPrice}>
                                                {amtPerSess > 0 && (
                                                    <span
                                                        style={{
                                                            textDecoration: 'line-through',
                                                            color: '#94A3B8',
                                                            fontSize: 12,
                                                            marginRight: 6,
                                                        }}
                                                    >
                                                        {formatCurrency(amtPerSess)}
                                                    </span>
                                                )}
                                                {formatCurrency(0)}
                                            </td>
                                            <td className={styles.detailTotal}>
                                                {amtPerSess > 0 && (
                                                    <span
                                                        style={{
                                                            textDecoration: 'line-through',
                                                            color: '#94A3B8',
                                                            fontSize: 12,
                                                            marginRight: 6,
                                                        }}
                                                    >
                                                        {formatCurrency(amtPerSess * g.count)}
                                                    </span>
                                                )}
                                                {formatCurrency(0)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* Summary */}
                        <div className={styles.summary}>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>Tổng cộng</span>
                                <span className={styles.summaryValue}>{formatCurrency(originalTotal)}</span>
                            </div>

                            {pureDiscount > 0 && (
                                <div className={styles.summaryRow}>
                                    <span className={styles.summaryLabel}>
                                        Giảm giá:
                                        {bill.discountType === '%' && originalTotal > 0 && (
                                            <span className={styles.summaryPointCash}>
                                                ({Math.round((pureDiscount / originalTotal) * 1000) / 10}% ={' '}
                                                {formatCurrency(pureDiscount)})
                                            </span>
                                        )}
                                    </span>
                                    <span className={`${styles.summaryValue} ${styles.summaryDiscount}`}>
                                        - {formatCurrency(pureDiscount)}
                                    </span>
                                </div>
                            )}

                            {pointUsed > 0 && !isKhachLe && (
                                <div className={styles.summaryRow}>
                                    <span className={styles.summaryLabel}>
                                        Điểm sử dụng:
                                        <span className={styles.summaryPointCash}>
                                            ({pointUsed} điểm = {formatCurrency(pointCash)})
                                        </span>
                                    </span>
                                    <span className={`${styles.summaryValue} ${styles.summaryPoints}`}>
                                        - {formatCurrency(pointCash)}
                                    </span>
                                </div>
                            )}

                            {pointReceived > 0 && !isKhachLe && (
                                <div className={styles.summaryRow}>
                                    <span className={styles.summaryLabel}>Điểm nhận được</span>
                                    <span className={`${styles.summaryValue} ${styles.summaryPointsEarned}`}>
                                        +{pointReceived} điểm
                                    </span>
                                </div>
                            )}

                            <div className={styles.summaryDivider} />

                            <div className={styles.summaryTotal}>
                                <span className={styles.summaryTotalLabel}>KHÁCH THANH TOÁN</span>
                                <span className={styles.summaryTotalValue}>{formatCurrency(total)}</span>
                            </div>

                            {owe > 0 && (
                                <div className={styles.summaryOwe}>
                                    <span>Còn nợ</span>
                                    <span>{formatCurrency(owe)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Ghi chú */}
                    {(bill.note || bill.internalNote) && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <span className={styles.cardTitle}>Ghi chú</span>
                            </div>
                            <div className={styles.cardBody}>
                                {bill.note && (
                                    <div
                                        className={styles.infoRow}
                                        style={{ marginBottom: bill.internalNote ? 16 : 0 }}
                                    >
                                        <span className={styles.infoLabel}>Ghi chú khách hàng</span>
                                        <span className={styles.noteText}>{bill.note}</span>
                                    </div>
                                )}
                                {bill.internalNote && (
                                    <div className={styles.infoRow}>
                                        <span className={styles.infoLabel}>Ghi chú nội bộ</span>
                                        <span className={styles.noteText}>{bill.internalNote}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    </>)}
                </div>

                {/* ===== CỘT PHẢI: Thông tin KH + QR ===== */}
                <div className={styles.layoutRight}>
                    {/* Thông tin khách hàng */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <span className={styles.cardTitle}>Thông tin khách hàng</span>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.customerInfoVertical}>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Khách hàng</span>
                                    <span className={styles.infoValue}>{customer.name || 'Khách lẻ'}</span>
                                </div>
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Số điện thoại</span>
                                    {canViewPhone && customer.phone ? (
                                        <span
                                            className={`${styles.infoValue} ${styles.copyable}`}
                                            onClick={() =>
                                                handleCopy(
                                                    customer.phone,
                                                    'Đã copy số điện thoại',
                                                    'Copy số điện thoại thất bại',
                                                )
                                            }
                                            title="Bấm để copy số điện thoại"
                                        >
                                            {formatPhoneNumber(customer.phone)}
                                            <Copy size={13} className={styles.copyIcon} />
                                        </span>
                                    ) : (
                                        <span className={styles.infoValue}>
                                            {customer.phone ? '****' : '-'}
                                        </span>
                                    )}
                                </div>
                                {!isKhachLe && (
                                    <div className={styles.infoRow}>
                                        <span className={styles.infoLabel}>Điểm tích luỹ</span>
                                        <span className={`${styles.infoValue} ${styles.pointsBadge}`}>
                                            <Star /> {bill.lastPoint ?? customer.point ?? 0} điểm
                                        </span>
                                    </div>
                                )}
                                <div className={styles.infoRow}>
                                    <span className={styles.infoLabel}>Nhân viên tạo đơn</span>
                                    <span className={styles.infoValue}>{employee.name || '-'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* QR Thanh toán */}
                    {bankInfo &&
                        (() => {
                            const qrAmount = bill.status !== 'paid' ? Math.round(owe > 0 ? owe : total) : '';
                            const qrAddInfo = encodeURIComponent((bill.code || '') + ' ' + (customer.name || ''));
                            const qrSmall =
                                bankInfo.qrCodeUrl ||
                                `https://img.vietqr.io/image/${bankInfo.bankBin}-${bankInfo.accountNumber}-qr_only.png?amount=${qrAmount}&addInfo=${qrAddInfo}`;
                            const qrFull = `https://img.vietqr.io/image/${bankInfo.bankBin}-${bankInfo.accountNumber}-print.png?amount=${qrAmount}&addInfo=${qrAddInfo}`;

                            return (
                                <>
                                    <div className={styles.card}>
                                        <div className={styles.cardHeader}>
                                            <span className={styles.cardTitle}>QR Thanh toán</span>
                                        </div>
                                        <div
                                            className={styles.qrBody}
                                            onClick={() => {
                                                setQrLoaded(false);
                                                setQrModalOpen(true);
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <img src={qrSmall} alt="QR Thanh toán" className={styles.qrImage} />
                                            <div className={styles.qrInfo}>
                                                {bill.status !== 'paid' && owe > 0 ? (
                                                    <>
                                                        <span className={styles.qrAmount}>{formatCurrency(owe)}</span>
                                                        <span className={styles.qrHint}>Số tiền còn nợ</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className={styles.qrAmount}>{formatCurrency(total)}</span>
                                                        <span className={styles.qrHint}>Nhấn để phóng to</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* QR Modal */}
                                    {qrModalOpen && (
                                        <div className={styles.qrModal} onClick={() => setQrModalOpen(false)}>
                                            <div className={styles.qrModalContent} onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    className={styles.qrModalClose}
                                                    onClick={() => setQrModalOpen(false)}
                                                >
                                                    <X />
                                                </button>
                                                <h3 className={styles.qrModalTitle}>Thanh toán</h3>
                                                <p className={styles.qrModalSub}>
                                                    {bill.code || `#${bill.id}`} — {customer.name || 'Khách hàng'}
                                                </p>
                                                {!qrLoaded && (
                                                    <div className={styles.qrModalLoading}>
                                                        <div className={styles.spinner} />
                                                        <span>Đang tải mã QR...</span>
                                                    </div>
                                                )}
                                                <img
                                                    src={qrFull}
                                                    alt="QR Full"
                                                    className={styles.qrModalImage}
                                                    style={{ display: qrLoaded ? 'block' : 'none' }}
                                                    onLoad={() => setQrLoaded(true)}
                                                />
                                                {qrLoaded && (
                                                    <>
                                                        <div className={styles.qrModalAmount}>
                                                            {formatCurrency(owe > 0 ? owe : total)}
                                                        </div>
                                                        <div className={styles.qrModalBrand}>
                                                            <img src={logo} alt="Navo" className={styles.qrModalLogo} />
                                                            <p className={styles.qrModalHint}>
                                                                <span className={styles.qrModalBrandName}>
                                                                    Navo Sổ Tiệm
                                                                </span>{' '}
                                                                dẫn lối vững vàng - Kinh doanh phát đạt
                                                            </p>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                </div>
            </div>

            {toast && (
                <div
                    className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}
                >
                    {toast.type === 'success' ? <CheckCircle size={15} /> : null}
                    {toast.text}
                </div>
            )}
        </div>
    );
}
